import React, { useMemo, useRef, useState } from "react";
import { CORECard } from "../../COREDesignSystem/Content/CORECard";
import { generateTestId } from "../../shared/testids/testids";
import { CORELoading } from "../../COREDesignSystem/Feedback/CORELoading";
import { COREEmpty } from "../../COREDesignSystem/Content/COREEmpty";
import { Col, Row } from "antd";
import { COREButton } from "../../COREDesignSystem/Action/COREButton";
import { ModalFeedback } from "../pages/feedback/ModalFeedback";
import { ProductType } from "./ProjectMarketplacePageWrapper";
import { useFeatureFlags } from "../../shared/customHoooks/useFeatureFlags";
import { COREError } from "../../COREDesignSystem/Content/COREError";
import {
  checkIsCandleStickChart,
  getLabelFromProduct,
  Product,
  ZoomLevel,
} from "./PriceHistoryChartWithFilter";
import {
  AggregatesChartProps,
  AggregatesChartV2,
  getTodayDate,
  Trace,
} from "../chartBuilder/AggregatesChartV2";
import moment from "moment/moment";
import {
  DataTitle,
  Layout,
  PlotType,
  Shape,
} from "plotly.js/dist/plotly-myPlotly.js";
import {
  ChartProps,
  COREAggregatesPanelChart,
  PlotRefType,
} from "../../COREDesignSystem/Chart/ChartHOC";
import { COREIcon } from "../../COREDesignSystem/Content/COREIcon";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";
import { ContentWithChartProps } from "../dashboards/WidgetPanel";
import {
  green140,
  grey160,
  grey60,
  indigo100,
} from "../../COREDesignSystem/Content/COREColour";
import { useTheme } from "../../theme/ThemeProvider";

export type PriceHistoryProps = {
  productType: ProductType;
  zoomLevel: ZoomLevel;
  products?: Product[];
  productName?: string;
  empty: React.ReactElement;
  isWidget: boolean;
  isCandleStickChart?: boolean;
} & Partial<ContentWithChartProps>;

const chartLayoutDefaults = (isDarkMode: boolean): Partial<Layout> => ({
  height: 450,
  xaxis: {
    autorange: true,
    showgrid: false,
    rangeslider: { visible: false },
    fixedrange: false,
  },
  yaxis: {
    domain: [0.1, 1],
    showticklabels: false,
    autorange: true,
    title: {
      text: "$ / Certificate",
    },
    showline: true,
    showgrid: true,
    fixedrange: false,
  },
  showlegend: false,
  hoverlabel: {
    bgcolor: isDarkMode ? green140 : indigo100,
    font: {
      color: "#FFF",
    },
  },
  margin: {
    t: 2,
    l: 35,
    r: 2,
    b: 2,
  },
});

const candleStickChartLayout = (isDarkMode: boolean) => {
  const config: Partial<Layout> = chartLayoutDefaults(isDarkMode);
  config.yaxis = {
    ...config.yaxis,
    domain: [0.43, 1],
    title: {
      ...(config.yaxis?.title as Partial<DataTitle>),
      text: "OHLC",
    },
  };

  config.yaxis2 = {
    domain: [0, 0.31],
    showticklabels: false,
    showgrid: true,
    showline: true,
    autorange: true,
    title: {
      text: "Volume",
    },
  };

  const lineStyle: Partial<Shape> = {
    type: "line",
    xref: "paper",
    yref: "paper",
    line: {
      color: isDarkMode ? grey160 : grey60,
      width: 1,
    },
    layer: "above",
  };

  config.shapes = [
    ...(config.shapes ?? []),
    {
      ...lineStyle,
      x0: 0,
      y0: 0,
      x1: 1,
      y1: 0,
    },
    {
      ...lineStyle,
      x0: 0,
      y0: 0.31,
      x1: 1,
      y1: 0.31,
    },
  ];
  return config;
};

const chartLayout = (
  isCandleStickChart: boolean,
  isDarkMode: boolean
): AggregatesChartProps["layout"] => {
  if (isCandleStickChart) {
    return candleStickChartLayout(isDarkMode);
  }
  return chartLayoutDefaults(isDarkMode);
};

const getChartTraces = (
  date: { from: moment.Moment; to: moment.Moment },
  isCandleStickChart: boolean,
  products?: Product[]
): Trace[] => {
  const aggFunc: string[] = isCandleStickChart
    ? ["first", "max", "min", "last", "sum"]
    : ["avg"];
  const candleStickTrace: Trace[] = [];

  aggFunc.forEach((d) => {
    products?.forEach((product: Product) => {
      const title = getLabelFromProduct(product);
      let chartType = "candlestick";
      let columnName = "trade_price";
      if (d === "sum") {
        chartType = "bar";
        columnName = "trade_volume";
      }
      if (d === "avg") {
        chartType = "scatter";
        columnName = "curve";
      }
      candleStickTrace.push({
        aggFunction: d,
        aggPeriod: "day",
        dateRange: {
          isRelative: false,
          bounds: "[)",
          ...date,
        },
        type: chartType as PlotType,
        axis: {
          side: "left",
          title: "MW",
        },
        name: "",
        title: { text: title },
        columnName: columnName,
        tableName: "trading",
        selectedValue: product.id,
        convertDataTo: "positive",
      });
    });
  });
  return candleStickTrace;
};

export const PriceHistoryChartPanel: React.FC<PriceHistoryProps> = ({
  productType,
  productName,
  zoomLevel,
  products,
  empty,
  isWidget,
  setOnDownloadPNG,
  setOnResetChart,
  setProgressPercent,
  isCandleStickChart: forceCandleStickChart = false,
}) => {
  const { isDarkTheme } = useTheme();
  const [feedbackModal, setFeedbackModal] = useState<boolean>(false);
  const chartRef = useRef(null);
  const chartLegendRef = useRef();
  const plotRef = useRef<PlotRefType>();
  const isCandleStickChart =
    checkIsCandleStickChart(productType) || forceCandleStickChart;

  const {
    sync: syncFeatureList,
    loading: loadingFeatureList,
    error: errorFeatureList,
    features: featureList,
  } = useFeatureFlags();

  const todayDate: string = getTodayDate("none").format();

  const chartProps: Omit<AggregatesChartProps & ChartProps, "size"> =
    useMemo(() => {
      const dateRange = {
        from: moment(todayDate).subtract(zoomLevel),
        to: moment(todayDate),
      };

      const dateRangeTitle = ` ${dateRange.from.format(
        "MMM D, YYYY"
      )} - ${dateRange.to.format("MMM D, YYYY")}`;
      const chartTitle = productName
        ? productName + dateRangeTitle
        : dateRangeTitle;
      const displayDownloadCsvButton =
        featureList &&
        featureList.includes(`chart-builder-environmental-product-tree-csv`);

      return {
        chartId: "project marketplace - price history",
        traces: getChartTraces(dateRange, isCandleStickChart, products),
        chartFilter: {
          hasTodayBreak: false,
          todayDate: todayDate,
        },
        downloadCsv: false,
        adjustHeightOfChart: true,
        downloadPng: displayDownloadCsvButton,
        layout: chartLayout(isCandleStickChart, isDarkTheme),
        testID: generateTestId(
          "projectmarketplace",
          "project-marketplace-price-history"
        ),
        hoverColor: "#00E9AC",
        showlegend: !isCandleStickChart,
        empty: empty,
        chartTitle: chartTitle,
        customLayout: true,
      };
    }, [
      empty,
      featureList,
      isCandleStickChart,
      isDarkTheme,
      productName,
      products,
      todayDate,
      zoomLevel,
    ]);

  if (loadingFeatureList && !syncFeatureList)
    return (
      <CORECard
        testID={generateTestId("projectmarketplace", "loading-chart-panel")}
        hoverable={false}
        elevation={"none"}
      >
        <CORELoading size={"lg"} />
      </CORECard>
    );

  if (!syncFeatureList) return null;
  if (errorFeatureList) {
    return <COREError />;
  }

  if (products && products.length <= 0)
    return (
      <CORECard
        testID={generateTestId("projectmarketplace", "no-select-chart-panel")}
        hoverable={false}
        elevation={"none"}
      >
        <COREEmpty
          description="No current price selection"
          hint="Make a selection from Type dropdown to display its price history"
          testID={generateTestId("projectmarketplace", "no-select-chart")}
        />
      </CORECard>
    );

  if (!featureList.includes("benchmarks-carbon") && !isCandleStickChart)
    return (
      <CORECard
        testID={generateTestId(
          "projectmarketplace",
          "no-permission-chart-panel"
        )}
        hoverable={false}
        elevation={"none"}
      >
        <COREEmpty
          description="No chart available"
          hint="Add Benchmarks to your CORE Markets subscription to gain insights into this product's historical price data and analysis"
          testID={generateTestId("projectmarketplace", "no-permission-chart")}
        />
        <Row justify="center">
          <Col>
            <COREButton
              onClick={() => setFeedbackModal(true)}
              icon={
                <COREIcon icon={icon({ name: "envelope", style: "regular" })} />
              }
              type="primary"
            >
              Contact us
            </COREButton>
          </Col>
        </Row>
        <ModalFeedback
          isFeedbackModalOpen={feedbackModal}
          setIsFeedbackModalOpen={setFeedbackModal}
        />
      </CORECard>
    );

  if (isWidget) {
    return (
      <AggregatesChartV2
        {...chartProps}
        chartLegendRef={chartLegendRef}
        plotRef={plotRef}
        ref={chartRef}
        chartPanel
        isWidget
        downloadPng={false}
        setOnDownloadPNG={setOnDownloadPNG}
        setOnResetChart={setOnResetChart}
        setProgressPercent={setProgressPercent}
      />
    );
  }

  return (
    <COREAggregatesPanelChart
      {...chartProps}
      roundCorner
      setOnDownloadPNG={setOnDownloadPNG}
      setOnResetChart={setOnResetChart}
      setProgressPercent={setProgressPercent}
      ref={chartRef}
      plotRef={plotRef}
      chartLegendRef={chartLegendRef}
    />
  );
};

export const PriceHistoryChart = (props: PriceHistoryProps) => (
  <PriceHistoryChartPanel {...props} isWidget={true} />
);
